"use client";

import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { EUR } from "~/lib/currency";
import { TherapyRelationshipType } from "~/prisma/browser";
import { type RouterOutput } from "~/server/api/root";
import { cn } from "~/lib/utils";

interface BookingSummaryProps {
  therapist: NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>;
  therapyType: TherapyRelationshipType;
  isIntroCall: boolean;
  selectedTerm?: string;
  className?: string;
}

const INTRO_CALL_DURATION_MINUTES = 15;

function getInitials(name: string | null | undefined): string {
  if (!name) return "";

  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function BookingSummary({
  therapist,
  therapyType,
  isIntroCall,
  selectedTerm,
  className,
}: BookingSummaryProps) {
  const isCouples = therapyType === TherapyRelationshipType.COUPLES;

  const sessionTypeLabel = isIntroCall
    ? "Uvodni razgovor"
    : isCouples
      ? "Terapija za parove"
      : "Individualna terapija";

  const durationMinutes = isIntroCall
    ? INTRO_CALL_DURATION_MINUTES
    : isCouples
      ? therapist.couplesSessionDuration
      : therapist.individualSessionDuration;

  const price = isCouples
    ? therapist.couplesTherapyPrice
    : therapist.defaultPrice;

  const priceLabel = isIntroCall
    ? "Besplatno"
    : price != null
      ? EUR.format(price)
      : null;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-md border bg-background p-3 md:px-4",
        className,
      )}
      data-testid="booking-summary"
    >
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage
          src={therapist.user.image ?? undefined}
          alt={therapist.user.name ?? ""}
        />
        <AvatarFallback className="text-xs font-semibold">
          {getInitials(therapist.user.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate text-sm font-semibold text-foreground">
          {therapist.user.name}
        </p>
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
          <span>{sessionTypeLabel}</span>
          <span aria-hidden="true">&middot;</span>
          <span>{durationMinutes} min</span>
          {priceLabel && (
            <>
              <span aria-hidden="true">&middot;</span>
              <span>{priceLabel}</span>
            </>
          )}
          {selectedTerm && (
            <>
              <span aria-hidden="true">&middot;</span>
              <span className="font-medium text-foreground">
                {selectedTerm}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
